import React, { useState } from "react";
import Field from "./common/Field";
import SectionHeading from "./common/SectionHeading";
import { getApprovalSubmitLabel } from "../utils/contractorDashboard";

export default function ApprovalSection({
  attestationAccepted,
  signedByName,
  signedDate,
  onAttestationChange,
  onSignedByNameChange,
  onSignedDateChange,
  onSubmit,
  onResubmit,
  onDelete,
  canDelete,
  statusMessage,
  status,
  hasActiveShift = false,
  submitLabel = "Submit timecard"
}) {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [resubmitReason, setResubmitReason] = useState("");
  const [showResubmitPrompt, setShowResubmitPrompt] = useState(false);
  const [resubmitting, setResubmitting] = useState(false);

  const isSubmitted = status === "submitted" || status === "approved";
  const primaryActionLabel = getApprovalSubmitLabel(status, submitLabel);
  const RESUBMIT_REASON_MAX_LENGTH = 1000;

  function handleDeleteClick() {
    setConfirmingDelete(true);
  }

  function handleConfirmDelete() {
    setConfirmingDelete(false);
    onDelete();
  }

  function handleCancelDelete() {
    setConfirmingDelete(false);
  }

  function handleResubmitClick() {
    setResubmitReason("");
    setShowResubmitPrompt(true);
  }

  async function handleConfirmResubmit() {
    const reason = resubmitReason.trim();
    // Guard against a double-click firing two resubmit emails: ignore while a request
    // is already in flight, and keep the button disabled until it resolves.
    if (!reason || resubmitting) {
      return;
    }
    setResubmitting(true);
    try {
      await onResubmit(reason);
      setShowResubmitPrompt(false);
      setResubmitReason("");
    } catch {
      // Leave the prompt open so the contractor can retry; the error is surfaced
      // through statusMessage by the parent.
    } finally {
      setResubmitting(false);
    }
  }

  function handleCancelResubmit() {
    if (resubmitting) {
      return;
    }
    setShowResubmitPrompt(false);
    setResubmitReason("");
  }

  return (
    <section className="sheet-section approval-section" aria-labelledby="approval-title">
      <SectionHeading
        title="Review and sign"
        titleId="approval-title"
      />

      <div className="signing-zone">
        <label className="attestation-check">
          <input
            type="checkbox"
            checked={attestationAccepted}
            onChange={(event) => onAttestationChange(event.target.checked)}
          />
          <span>I attest that the above information is correct.</span>
        </label>

        <div className="field-grid signature-grid">
          <Field id="signature-name" label="Name">
            <input
              id="signature-name"
              type="text"
              value={signedByName}
              onChange={(event) => onSignedByNameChange(event.target.value)}
            />
          </Field>

          <Field id="signed-date" label="Date">
            <input
              id="signed-date"
              type="date"
              value={signedDate}
              onChange={(event) => onSignedDateChange(event.target.value)}
            />
          </Field>
        </div>
      </div>

      <div className="submission-footer">
        {(status === "submitted" || status === "approved") && (
          <p className="timecard-submitted-notice">
            This timecard has been submitted.
          </p>
        )}
        <div className="action-row" aria-label="Timecard actions">
          {!showResubmitPrompt && (
            <div>
              <button
                type="button"
                className="primary-action"
                onClick={isSubmitted ? handleResubmitClick : onSubmit}
                disabled={hasActiveShift}
                aria-describedby={hasActiveShift ? "submit-active-shift-hint" : undefined}
              >
                {primaryActionLabel}
              </button>
              {hasActiveShift && (
                <p id="submit-active-shift-hint" className="submit-blocked-hint">
                  Available after you clock out of all shifts.
                </p>
              )}
            </div>
          )}
          {canDelete && !confirmingDelete ? (
            <button
              type="button"
              className="secondary-action"
              onClick={handleDeleteClick}
            >
              Delete timecard
            </button>
          ) : null}
          {canDelete && confirmingDelete ? (
            <>
              <button
                type="button"
                className="danger-action"
                onClick={handleConfirmDelete}
              >
                Yes, delete
              </button>
              <button
                type="button"
                className="secondary-action"
                onClick={handleCancelDelete}
              >
                Cancel
              </button>
            </>
          ) : null}
        </div>

        {showResubmitPrompt ? (
          <div className="resubmit-prompt" role="group" aria-label="Resubmit reason">
            <label htmlFor="resubmit-reason">
              Please enter a reason for resubmitting this timecard:
            </label>
            <textarea
              id="resubmit-reason"
              className="resubmit-reason-input"
              value={resubmitReason}
              onChange={(event) => setResubmitReason(event.target.value)}
              maxLength={RESUBMIT_REASON_MAX_LENGTH}
              rows={3}
            />
            <div className="action-row">
              <button
                type="button"
                className="primary-action"
                onClick={handleConfirmResubmit}
                disabled={!resubmitReason.trim() || resubmitting}
              >
                {resubmitting ? "Resubmitting…" : "Resubmit"}
              </button>
              <button
                type="button"
                className="secondary-action"
                onClick={handleCancelResubmit}
                disabled={resubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {statusMessage ? (
        <div className="status-message error" role="alert">
          {statusMessage}
        </div>
      ) : null}
    </section>
  );
}
